<template>
    <div class="page-header">
        <div class="page-header-heading">
            <div class="breadcrumb-help-wrap" v-help="helpKey">
                <BaseBreadcrumb :breadcrumbs="breadcrumbs" />
            </div>
            <div v-if="route.meta.teachingGridAppearance" class="page-header-appearance">
                <TeachingGridDensity :key="String(route.name)" />
            </div>
        </div>

        <!-- Desktop: the context toolbar sits inline at the right of the header. -->
        <ContextToolbar v-if="!smAndDown" :highlight="currentRouteHighlight" />

        <!-- Mobile / tablet (#116): collapse the toolbar behind a filter button so it no
             longer eats vertical space above the page. Tapping it reveals the filters on
             their own full-width row; a dot marks that a filter is active while collapsed. -->
        <template v-else-if="hasFilters">
            <v-btn :variant="showFilters ? 'tonal' : 'text'" :color="showFilters ? 'primary' : undefined" icon
                size="small" :title="showFilters ? 'Masquer les filtres' : 'Filtres'"
                @click="showFilters = !showFilters">
                <v-badge :model-value="hasActiveSelection && !showFilters" color="primary" dot>
                    <v-icon>fa-filter</v-icon>
                </v-badge>
            </v-btn>
            <v-expand-transition>
                <div v-if="showFilters" class="ctx-mobile-wrap">
                    <ContextToolbar :highlight="currentRouteHighlight" />
                </div>
            </v-expand-transition>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import { BaseBreadcrumb } from '@fondationcss/ui';
import ContextToolbar from '@/components/context-toolbar.vue';
import TeachingGridDensity from '@/components/teaching-grid-density.vue';
import { usePageHeaderStore } from '@/stores/page-header-store';
import { pageHeaderContent } from '@/composables/use-page-breadcrumbs';
import { useContextToolbarStore } from '@/stores/context-toolbar-store';
import { useSdk as useCoreSdk } from '@fondationcss/sdk';
import { isGroupTrainingEnabled } from '@/utils/group-training-feature';

const store = usePageHeaderStore();
const ctxStore = useContextToolbarStore();
const coreSdk = useCoreSdk();
const route = useRoute();
const { smAndDown } = useDisplay();

// Collapsed by default on mobile; toggled by the filter button.
const showFilters = ref(false);

// Same default as ContextToolbar — a route with no context dropdowns has nothing to collapse.
// The Groupe axis follows the « Formation par groupes » flag (#812), like the toolbar itself:
// a route whose ONLY axis is the group must not offer an empty filter panel.
const DEFAULT_CONTEXT_DROPDOWNS = ['site', 'program', 'group', 'teacher'];
const hasFilters = computed(() => {
    const axes = (route.meta.contextToolbar as string[] | undefined) ?? DEFAULT_CONTEXT_DROPDOWNS;
    const visible = isGroupTrainingEnabled(coreSdk.Features.flags.value)
        ? axes
        : axes.filter(axis => axis !== 'group');
    return visible.length > 0;
});

// Drives the "active filter" dot so the user sees a context is applied while collapsed.
const hasActiveSelection = computed(() =>
    !!(ctxStore.selectedSite || ctxStore.selectedProgram || ctxStore.selectedGroup || ctxStore.selectedTeacher)
);

const currentRouteHighlight = computed(() =>
    ctxStore.highlightRoute === (route.name ?? null) ? ctxStore.highlight : []
);

// A trail stamped by another route is stale — the page we are on never wrote it.
// Applies to routes that mount a Fondation component (the legal documents) or
// simply never call usePageBreadcrumbs; they used to inherit the previous page's
// trail and its help key (#715). The decision itself lives in the composable so a
// test can exercise the real thing instead of a copy of it.
const content = computed(() => pageHeaderContent(store, route));

const helpKey = computed(() => content.value.helpKey);
const breadcrumbs = computed(() => content.value.breadcrumbs);
</script>

<style scoped>
.page-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-shrink: 0;
    min-height: 40px;
    gap: 16px;
}

.page-header-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.page-header-appearance {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

/* v-help appends its `?` trigger as a child of this element. Keeping it
   inline-flex lays the trigger out beside the breadcrumb instead of below it. */
.breadcrumb-help-wrap {
    display: flex;
    align-items: center;
}

/* Keep the round `?` trigger from being squeezed into an oval as a flex item. */
.breadcrumb-help-wrap :deep(.fondation-help-trigger) {
    flex-shrink: 0;
}

/* BaseBreadcrumb's bare variant ships a 10px bottom margin for when it sits
   alone above content. In this flex row the margin box is what gets centered,
   which pushed the appended `?` trigger above the trail's centerline. */
.breadcrumb-help-wrap :deep(.base-breadcrumb) {
    margin-bottom: 0;
}

/* Mobile / tablet (#116): the breadcrumb keeps the first row with the filter button at
   its right (via space-between); the revealed toolbar wraps onto its own full-width row. */
@media (max-width: 959px) {
    .page-header {
        flex-wrap: wrap;
        gap: 8px;
    }

    .ctx-mobile-wrap {
        flex: 1 1 100%;
    }
}
</style>
